import { useState, useEffect } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import {
  Location,
  Service,
  Booking,
  Customer,
  Settings,
  DataRetentionLog
} from '../types'
import {
  mapDatabaseBookingToBooking,
  mapBookingToDatabase,
  convertLocation,
  convertService,
} from '../utils/typeUtils'

// Lokaler Type für Datenschutz-Einstellungen
interface PrivacySettings {
  dataRetentionDays: number;
  automaticCleanup: boolean;
  anonymizationEnabled: boolean;
  complianceNote: string;
}

const normalizeTime = (time: string): string => (time ?? '').slice(0, 5);

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = normalizeTime(time).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return hours * 60 + minutes;
};

const rangesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number
): boolean => startA < endB && endA > startB;

const firstRelation = <T,>(value: T | T[] | null | undefined): T | undefined => {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
};

// Hook für alle Supabase-Daten
export const useSupabaseData = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [settings, setSettings] = useState<Settings[]>([]);
  const [retentionLogs, setRetentionLogs] = useState<DataRetentionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Leere Arrays als Fallback (keine Mock-Daten mehr)
  const getMockData = () => {
    const mockLocations: Location[] = [];
    const mockServices: Service[] = [];
    const mockCustomers: Customer[] = [];
    const mockBookings: Booking[] = [];

    return {
      locations: mockLocations,
      services: mockServices,
      customers: mockCustomers,
      bookings: mockBookings,
      settings: [],
      retentionLogs: []
    };
  };

  // Lade alle Daten
  const fetchAllData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Verwende Mock-Daten wenn Supabase nicht konfiguriert ist
      if (!isSupabaseConfigured) {
        const mockData = getMockData();
        
        setLocations(mockData.locations.map(convertLocation))
        setServices(mockData.services.map(convertService))
        setCustomers(mockData.customers)
        setBookings(mockData.bookings)
        setSettings(mockData.settings)
        setRetentionLogs(mockData.retentionLogs)
        
        return;
      }


      const [
        locationsResponse,
        servicesResponse,
        bookingsResponse,
        customersResponse,
        settingsResponse,
        retentionLogsResponse
      ] = await Promise.all([
        supabase.from('locations').select('*').order('name'),
        supabase.from('services').select('*').order('category', { ascending: true }),
        supabase.from('bookings').select('*, employees(*)').order('booking_date', { ascending: false }),
        supabase.from('customers').select('*').order('created_at', { ascending: false }),
        supabase.from('settings').select('*'),
        supabase.from('data_retention_logs').select('*').order('executed_at', { ascending: false }).limit(100)
      ])

      // Fehlerbehandlung
      if (locationsResponse.error) throw locationsResponse.error
      if (servicesResponse.error) throw servicesResponse.error
      if (bookingsResponse.error) throw bookingsResponse.error
      if (customersResponse.error) throw customersResponse.error
      if (settingsResponse.error) throw settingsResponse.error
      if (retentionLogsResponse.error) throw retentionLogsResponse.error

      // Daten konvertieren
      const convertedLocations = (locationsResponse.data || []).map(convertLocation);
      const convertedServices  = (servicesResponse.data || []).map(convertService);
      const convertedBookings  = (bookingsResponse.data || []).map(mapDatabaseBookingToBooking);

      // Customer- & Service-Objekte in Buchungen einbetten
      const enrichedBookings = convertedBookings.map(b => {
        const customerObj = (customersResponse.data || []).find(c => c.id === b.customerId);
        const serviceObj  = convertedServices.find(s => s.id === b.serviceId);
        return { ...b, customer: customerObj, service: serviceObj } as typeof b & { customer?: any; service?: any };
      });

      // State setzen
      setLocations(convertedLocations);
      setServices(convertedServices);
      setBookings(enrichedBookings as any);
      setCustomers(customersResponse.data || []);
      setSettings(settingsResponse.data || []);
      setRetentionLogs(retentionLogsResponse.data || []);


    } catch (err) {
      console.error('❌ Fehler beim Laden der Daten:', err)
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  // Erstelle neue Buchung mit automatischer Kundenerstellung
  const createBooking = async (bookingData: {
    locationId: number;
    serviceId: number;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    customerAddress?: string;
    bookingDate: string;
    bookingTime: string;
    employeeId?: number;
    durationOverride?: number;
    needsPrepayment?: boolean;
    prepaymentAmount?: number;
    paymentMethod?: string;
  }) => {
    try {
      const selectedService = services.find(s => Number(s.id) === Number(bookingData.serviceId));
      const selectedLocation = locations.find(l => Number(l.id) === Number(bookingData.locationId));
      const requestedDuration = bookingData.durationOverride ?? selectedService?.duration ?? 60;
      const requestedBuffer = selectedLocation?.bufferTime ?? selectedLocation?.buffer_time ?? 0;
      const requestedStart = timeToMinutes(bookingData.bookingTime);
      const requestedEnd = requestedStart + requestedDuration + requestedBuffer;

      // Mock-Modus: Simuliere erfolgreiche Buchung
      if (!isSupabaseConfigured) {
        const conflict = bookings.find(existing => {
          if (existing.bookingDate !== bookingData.bookingDate) return false;
          if (existing.status === 'cancelled') return false;
          if (bookingData.employeeId && existing.employeeId && existing.employeeId !== bookingData.employeeId) return false;

          const existingService = services.find(s => Number(s.id) === Number(existing.serviceId));
          const existingLocation = locations.find(l => Number(l.id) === Number(existing.locationId));
          const existingStart = timeToMinutes(existing.bookingTime);
          const existingDuration = existingService?.duration ?? 60;
          const existingBuffer = existingLocation?.bufferTime ?? existingLocation?.buffer_time ?? 0;
          const existingEnd = existingStart + existingDuration + existingBuffer;

          return rangesOverlap(requestedStart, requestedEnd, existingStart, existingEnd);
        });

        if (conflict) {
          throw new Error(`Diese Uhrzeit ueberschneidet sich mit einem bestehenden Termin um ${normalizeTime(conflict.bookingTime)} Uhr.`);
        }

        // Füge zur lokalen Liste hinzu
        const newBooking: Booking = {
          id: Date.now(),
          // snake_case (required by interface)
          service_id: bookingData.serviceId,
          location_id: bookingData.locationId,
          customer_id: 1,
          employee_id: bookingData.employeeId,
          booking_date: bookingData.bookingDate,
          booking_time: bookingData.bookingTime,
          // camelCase (convenience)
          serviceId: bookingData.serviceId,
          locationId: bookingData.locationId,
          customerId: 1,
          employeeId: bookingData.employeeId,
          bookingDate: bookingData.bookingDate,
          bookingTime: bookingData.bookingTime,
          needsPrepayment: bookingData.needsPrepayment || false,
          prepaymentAmount: bookingData.prepaymentAmount || 0,
          status: 'confirmed',
          reminderSent: false,
          paymentMethod: bookingData.paymentMethod,
          created: new Date().toISOString()
        };
        
        setBookings(prev => [newBooking, ...prev]);
        return newBooking;
      }

      const [serviceCheck, locationCheck, existingBookingsCheck] = await Promise.all([
        selectedService
          ? Promise.resolve({ data: selectedService, error: null })
          : supabase
              .from('services')
              .select('id,name,duration')
              .eq('id', bookingData.serviceId)
              .single(),
        selectedLocation
          ? Promise.resolve({ data: selectedLocation, error: null })
          : supabase
              .from('locations')
              .select('id,buffer_time')
              .eq('id', bookingData.locationId)
              .single(),
        supabase
          .from('bookings')
          .select('id,status,booking_time,service_id,location_id,employee_id,service:services(id,name,duration),location:locations(id,name,buffer_time),employee:employees(id,name)')
          .eq('booking_date', bookingData.bookingDate)
          .neq('status', 'cancelled'),
      ]);

      if (serviceCheck.error) throw serviceCheck.error;
      if (locationCheck.error) throw locationCheck.error;
      if (existingBookingsCheck.error) throw existingBookingsCheck.error;

      const serviceForCheck = serviceCheck.data as Pick<Service, 'id' | 'name' | 'duration'>;
      const locationForCheck = locationCheck.data as Pick<Location, 'id' | 'buffer_time' | 'bufferTime'>;
      const durationForCheck = bookingData.durationOverride ?? serviceForCheck?.duration ?? 60;
      const bufferForCheck = locationForCheck?.bufferTime ?? locationForCheck?.buffer_time ?? 0;
      const startForCheck = timeToMinutes(bookingData.bookingTime);
      const endForCheck = startForCheck + durationForCheck + bufferForCheck;

      const conflictingBooking = (existingBookingsCheck.data || []).find((existing: any) => {
        if (bookingData.employeeId && existing.employee_id && Number(existing.employee_id) !== Number(bookingData.employeeId)) {
          return false;
        }

        const existingService = firstRelation(existing.service) as Pick<Service, 'duration' | 'name'> | undefined;
        const existingLocation = firstRelation(existing.location) as Pick<Location, 'buffer_time' | 'name'> | undefined;
        const existingStart = timeToMinutes(existing.booking_time);
        const existingDuration = existingService?.duration ?? 60;
        const existingBuffer = existingLocation?.buffer_time ?? bufferForCheck;
        const existingEnd = existingStart + existingDuration + existingBuffer;

        return rangesOverlap(startForCheck, endForCheck, existingStart, existingEnd);
      });

      if (conflictingBooking) {
        const conflictTime = normalizeTime(conflictingBooking.booking_time);
        const employeeInfo = conflictingBooking.employee_id
          ? ` bei ${firstRelation(conflictingBooking.employee)?.name ?? 'diesem Mitarbeiter'}`
          : '';
        throw new Error(`Diese Uhrzeit ist nicht frei${employeeInfo}. Sie ueberschneidet sich mit einem bestehenden Termin um ${conflictTime} Uhr.`);
      }

      // Schritt 1: Suche oder erstelle Kunden
      let customer_id: number
      
      // Suche nach existierendem Kunden — bevorzugt per E-Mail, Fallback per Name
      let existingCustomers: any[] | null = null;
      let searchError: any = null;

      if (bookingData.customerEmail) {
        ({ data: existingCustomers, error: searchError } = await supabase
          .from('customers')
          .select('id, name, email, phone, is_anonymized')
          .eq('email', bookingData.customerEmail)
          .eq('is_anonymized', false));
      }

      // Falls per E-Mail nichts gefunden, nach Name suchen
      if (!existingCustomers?.length) {
        ({ data: existingCustomers, error: searchError } = await supabase
          .from('customers')
          .select('id, name, email, phone, is_anonymized')
          .eq('name', bookingData.customerName)
          .eq('is_anonymized', false));
      }

      if (searchError) throw searchError;

      if (existingCustomers && existingCustomers.length > 0) {
        customer_id = existingCustomers[0].id;
      } else {
        // Erstelle neuen Kunden
        const { data: newCustomer, error: customerError } = await supabase
          .from('customers')
          .insert({
            name: bookingData.customerName,
            email: bookingData.customerEmail,
            phone: bookingData.customerPhone,
            address: bookingData.customerAddress,
          })
          .select()
          .single()

        if (customerError) throw customerError
        customer_id = newCustomer.id
      }

      // Schritt 2: Erstelle Buchung
      try {
        const newBookingForDb = mapBookingToDatabase({
          ...bookingData,
          customerId: customer_id,
          customer_id: customer_id,
          employeeId: bookingData.employeeId,
          employee_id: bookingData.employeeId,
          status: 'confirmed'
        } as unknown as Booking);


        const { error: bookingError, data: newBookingData } = await supabase
          .from('bookings')
          .insert(newBookingForDb)
          .select()
          .single();

        if (bookingError) throw bookingError;

        
        // Aktualisiere lokale Daten
        await fetchAllData();
        
        return mapDatabaseBookingToBooking(newBookingData);

      } catch (err) {
        console.error('❌ Fehler beim Erstellen der Buchung:', err);
        throw err
      }

    } catch (err) {
      console.error('❌ Fehler beim Erstellen der Buchung:', err);
      throw err
    }
  }

  // Datenschutz-Funktionen
  const getPrivacySettings = (): PrivacySettings => {
    const privacySettings = settings.find(s => s.key === 'privacy')
    return privacySettings?.value || {
      dataRetentionDays: 30,
      automaticCleanup: true,
      anonymizationEnabled: true,
      complianceNote: "Kundendaten werden nach 30 Tagen nach dem letzten Termin anonymisiert"
    }
  }

  const updatePrivacySettings = async (newSettings: Partial<PrivacySettings>) => {
    try {

      const currentSettings = getPrivacySettings()
      const updatedSettings = { ...currentSettings, ...newSettings }

      const { error } = await supabase
        .from('settings')
        .upsert({
          key: 'privacy',
          value: updatedSettings,
          updated_at: new Date().toISOString()
        })

      if (error) throw error

      await fetchAllData()

    } catch (err) {
      console.error('❌ Fehler beim Aktualisieren der Datenschutz-Einstellungen:', err)
      throw err
    }
  }

  const runDataRetentionCleanup = async () => {
    try {

      // Mock-Modus: Simuliere Bereinigung
      if (!isSupabaseConfigured) {
        return {
          success: true,
          message: 'Mock-Bereinigung erfolgreich (Supabase nicht konfiguriert)'
        };
      }

      // Rufe die Datenschutz-Bereinigungsfunktionen auf
      const [anonymizeResult, cleanupResult] = await Promise.all([
        supabase.rpc('anonymize_customer_data'),
        supabase.rpc('cleanup_old_anonymized_data')
      ])

      if (anonymizeResult.error) throw anonymizeResult.error
      if (cleanupResult.error) throw cleanupResult.error

      
      // Aktualisiere lokale Daten
      await fetchAllData()

      return {
        success: true,
        message: 'Datenschutz-Bereinigung erfolgreich abgeschlossen'
      }

    } catch (err) {
      console.error('❌ Fehler bei der Datenschutz-Bereinigung:', err)
      throw err
    }
  }

  // Standort aktualisieren
  const updateLocation = async (id: number, data: Partial<Location>): Promise<void> => {
    try {

      if (!isSupabaseConfigured) {
        // Im Mock-Modus nur lokale Liste patchen
        setLocations(prev => prev.map(l => l.id === id ? ({ ...l, ...data } as any) : l));
        return;
      }

      // Schlüssel-Mapping: camelCase → snake_case
      const payload: any = {};
      if ('name' in data) payload.name = data.name;
      if ('address' in data) payload.address = data.address;
      if ('phone' in data) payload.phone = data.phone;
      if ('email' in data) payload.email = data.email;
      if ('active' in data) payload.active = data.active;
      if ('individualOpenTimes' in data) payload.individual_open_times = JSON.stringify((data as any).individualOpenTimes);
      if ('slotDuration' in data) payload.slot_duration = (data as any).slotDuration;
      if ('bufferTime' in data) payload.buffer_time = (data as any).bufferTime;
      if ('lunchWindowStart' in data) payload.lunch_break_start = (data as any).lunchWindowStart;
      if ('lunchWindowEnd' in data) payload.lunch_break_end = (data as any).lunchWindowEnd;
      if ('lunchBreakDuration' in data) payload.lunch_break_duration = (data as any).lunchBreakDuration;

      const { error } = await supabase
        .from('locations')
        .update(payload)
        .eq('id', id);

      if (error) throw error;

      // Daten neu laden
      await fetchAllData();
    } catch (err) {
      console.error('❌ Fehler beim Aktualisieren des Standorts:', err);
      throw err;
    }
  };

  const createCustomer = async (data: { name: string; email?: string; phone?: string; address?: string; notes?: string }): Promise<void> => {
    try {
      if (!isSupabaseConfigured) {
        const mock: import('../types').Customer = { id: Date.now(), name: data.name, email: data.email || '', phone: data.phone || '', address: data.address || '', notes: data.notes || '' };
        setCustomers(prev => [mock, ...prev]);
        return;
      }
      const { data: created, error } = await supabase.from('customers').insert({
        name: data.name.trim(),
        email: data.email?.trim() || null,
        phone: data.phone?.trim() || null,
        address: data.address?.trim() || null,
        notes: data.notes?.trim() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).select().single();
      if (error) throw error;
      if (created) setCustomers(prev => [created as import('../types').Customer, ...prev]);
    } catch (err) {
      console.error('❌ Fehler beim Erstellen des Kunden:', err);
      throw err;
    }
  };

  const updateCustomer = async (id: number, data: Partial<import('../types').Customer>): Promise<void> => {
    try {
      if (!isSupabaseConfigured) {
        setCustomers(prev => prev.map(c => c.id === id ? ({ ...c, ...data }) : c));
        return;
      }
      const payload: Record<string, unknown> = {};
      if ('name' in data)            payload.name = data.name;
      if ('email' in data)           payload.email = data.email;
      if ('phone' in data)           payload.phone = data.phone;
      if ('address' in data)         payload.address = data.address;
      if ('notes' in data)           payload.notes = data.notes;
      if ('birthday' in data)        payload.birthday = data.birthday || null;
      if ('allergies' in data)       payload.allergies = data.allergies || null;
      if ('is_regular' in data)      payload.is_regular = data.is_regular;
      if ('discount' in data)        payload.discount = data.discount ?? 0;
      if ('custom_duration' in data) payload.custom_duration = data.custom_duration || null;
      if ('service_durations' in data) payload.service_durations = data.service_durations || {};
      payload.updated_at = new Date().toISOString();
      const { error } = await supabase.from('customers').update(payload).eq('id', id);
      if (error) throw error;
      await fetchAllData();
    } catch (err) {
      console.error('❌ Fehler beim Aktualisieren des Kunden:', err);
      throw err;
    }
  };

  const anonymizeCustomer = async (id: number): Promise<void> => {
    try {
      if (!isSupabaseConfigured) {
        setCustomers(prev => prev.map(c => c.id === id ? ({
          ...c, name: 'Anonymisiert', email: '', phone: '',
          address: '', notes: '', birthday: undefined, allergies: undefined,
          isAnonymized: true,
        } as Customer) : c));
        return;
      }
      const { error } = await supabase.from('customers').update({
        name: 'Anonymisiert',
        email: null, phone: null, address: null, notes: null,
        birthday: null, allergies: null, is_anonymized: true,
        anonymized_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', id);
      if (error) throw error;
      await fetchAllData();
    } catch (err) {
      console.error('❌ Fehler beim Anonymisieren:', err);
      throw err;
    }
  };

  const getCustomerDataStats = () => {
    const totalCustomers = customers.length
    const anonymizedCustomers = customers.filter(c => c.isAnonymized).length
    const activeCustomers = totalCustomers - anonymizedCustomers
    
    const recentRetentionActions = retentionLogs.filter(
      log => new Date(log.executed_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    )

    return {
      totalCustomers,
      activeCustomers,
      anonymizedCustomers,
      eligibleForCleanup: isSupabaseConfigured ? 0 : 0, // Mock: keine bereinigungs-berechtigen Kunden
      retentionLogs: retentionLogs.length,
      recentAnonymizations: recentRetentionActions.filter(log => log.action === 'anonymized').length,
      recentDeletions: recentRetentionActions.filter(log => log.action === 'deleted').length,
      lastCleanup: retentionLogs.length > 0 ? retentionLogs[0].executed_at : null
    }
  }

  // Lade Daten beim ersten Rendern
  useEffect(() => {
    fetchAllData().catch(err => {
      console.error('🚨 Fehler beim initialen Laden:', err);
      // Im Mock-Modus soll die App trotzdem funktionieren
      if (!isSupabaseConfigured) {
        const mockData = getMockData();
        setLocations(mockData.locations);
        setServices(mockData.services);
        setCustomers(mockData.customers);
        setBookings(mockData.bookings);
        setSettings(mockData.settings);
        setRetentionLogs(mockData.retentionLogs);
        setError(null); // Fehler zurücksetzen für Mock-Modus
        setLoading(false);
      }
    });
  }, [])

  return {
    locations,
    services,
    bookings,
    customers,
    settings,
    retentionLogs,
    loading,
    error,
    createBooking,
    setLocations,
    setServices,
    setBookings,
    setCustomers,
    setSettings,
    setRetentionLogs,
    getPrivacySettings,
    updatePrivacySettings,
    runDataRetentionCleanup,
    getCustomerDataStats,
    convertLocation,
    convertService,
    updateLocation,
    createCustomer,
    updateCustomer,
    anonymizeCustomer,
  }
}
